/**
 * gbi-hero — cinematic hero animations.
 *
 * Per-hero <section data-hero>:
 *   - data-hero-title    -> split into spans with stagger reveal
 *   - data-hero-bg       -> translated on mousemove for parallax
 *   - data-hero-parallax -> max px shift (default 12)
 *   - [data-counter]     -> animated count-up on intersection
 *     - data-counter-suffix -> appended after value
 *     - data-counter-format="k" -> 45000 -> "45K"
 *
 * prefers-reduced-motion: chars appear instantly, parallax disabled, counters jump to final.
 */
(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  function splitTitle(el) {
    const text = el.textContent;
    while (el.firstChild) el.removeChild(el.firstChild);
    const chars = [];
    const words = text.split(/(\s+)/); // keep separators so newlines and multiple spaces survive
    let charIdx = 0;
    for (let w = 0; w < words.length; w++) {
      const word = words[w];
      if (/^\s+$/.test(word)) {
        // Preserve whitespace as a text node so layout shows spaces between words
        el.appendChild(document.createTextNode(word));
        continue;
      }
      const wordWrap = document.createElement('span');
      wordWrap.className = 'word';
      wordWrap.style.display = 'inline-block';
      wordWrap.style.whiteSpace = 'nowrap';
      for (let i = 0; i < word.length; i++) {
        const span = document.createElement('span');
        span.className = 'char';
        span.textContent = word[i];
        span.style.transitionDelay = `${charIdx * 25}ms`;
        wordWrap.appendChild(span);
        chars.push(span);
        charIdx++;
      }
      el.appendChild(wordWrap);
    }
    return chars;
  }

  function revealTitle(el) {
    const chars = splitTitle(el);
    if (reduceMotion) {
      chars.forEach((c) => c.classList.add('is-revealed'));
      return;
    }
    requestAnimationFrame(() => {
      chars.forEach((c) => c.classList.add('is-revealed'));
    });
  }

  function parallax(hero) {
    const bg = hero.querySelector('[data-hero-bg]');
    if (!bg || reduceMotion) return;
    const intensity = parseFloat(hero.dataset.heroParallax) || 12;
    let raf = false, nx = 0, ny = 0;
    function apply() {
      raf = false;
      bg.style.transform = `translate3d(${nx}px, ${ny}px, 0)`;
    }
    hero.addEventListener('mousemove', (e) => {
      const r = hero.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      nx = -px * intensity * 2;
      ny = -py * intensity * 2;
      if (!raf) { raf = true; requestAnimationFrame(apply); }
    });
    hero.addEventListener('mouseleave', () => {
      nx = 0; ny = 0;
      if (!raf) { raf = true; requestAnimationFrame(apply); }
    });
  }

  function formatCount(value, format) {
    if (format === 'k' && value >= 1000) {
      const decimal = value % 1000 === 0 ? 0 : 1;
      return `${(value / 1000).toFixed(decimal)}K`;
    }
    return Math.floor(value).toLocaleString();
  }

  function animateCounter(el) {
    const target = parseFloat(el.dataset.counter) || 0;
    const prefix = el.dataset.counterPrefix || '';
    const suffix = el.dataset.counterSuffix || '';
    const format = el.dataset.counterFormat || '';
    const duration = parseFloat(el.dataset.counterDuration) || 2000;

    if (reduceMotion) {
      el.textContent = `${prefix}${formatCount(target, format)}${suffix}`;
      return;
    }

    let start = null;
    function step(t) {
      if (!start) start = t;
      const progress = Math.min(1, (t - start) / duration);
      const value = easeOutCubic(progress) * target;
      el.textContent = `${prefix}${formatCount(value, format)}${suffix}`;
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function init(root) {
    (root || document).querySelectorAll('[data-hero]:not([data-hero-init])').forEach((hero) => {
      hero.setAttribute('data-hero-init', '');
      const title = hero.querySelector('[data-hero-title]');
      if (title) {
        const io = new IntersectionObserver((entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              revealTitle(title);
              obs.disconnect();
            }
          });
        }, { threshold: 0.2 });
        io.observe(title);
      }
      parallax(hero);

      hero.querySelectorAll('[data-counter]').forEach((c) => {
        const io = new IntersectionObserver((entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              animateCounter(c);
              obs.disconnect();
            }
          });
        }, { threshold: 0.4 });
        io.observe(c);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  window.GBI = window.GBI || {};
  window.GBI.hero = { init };
})();
